/**
 * User Context for Tiered Access Management
 * Handles userId persistence in localStorage and user state management
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate a unique user ID
const generateUserId = () => {
  return 'user_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
};

// Get or create userId from localStorage
const getStoredUserId = () => {
  let userId = localStorage.getItem('swoopy_user_id');
  if (!userId) {
    userId = generateUserId();
    localStorage.setItem('swoopy_user_id', userId);
  }
  return userId;
};

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [userId] = useState(getStoredUserId);
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user status from backend
  const fetchUserStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setUserStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch user status:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Initialize user on mount
  useEffect(() => {
    fetchUserStatus();
  }, [fetchUserStatus]);

  // Upgrade to Basic plan
  const upgradeToBasic = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/${userId}/upgrade/basic`, {
        method: 'POST',
      });
      if (response.ok) {
        const data = await response.json();
        setUserStatus(data.userStatus);
        return data;
      }
    } catch (error) {
      console.error('Failed to upgrade to basic:', error);
    }
    return null;
  };

  // Upgrade to Pro plan
  const upgradeToPro = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/${userId}/upgrade/pro`, {
        method: 'POST',
      });
      if (response.ok) {
        const data = await response.json();
        setUserStatus(data.userStatus);
        return data;
      }
    } catch (error) {
      console.error('Failed to upgrade to pro:', error);
    }
    return null;
  };

  // Record surprise views
  const recordViews = async (count = 1) => {
    try {
      const response = await fetch(`${API_URL}/api/user/${userId}/view-surprises?count=${count}`, {
        method: 'POST',
      });
      if (response.ok) {
        const data = await response.json();
        setUserStatus(data);
        return data;
      }
    } catch (error) {
      console.error('Failed to record views:', error);
    }
    return null;
  };

  // Mock unlock for testing (only works when MOCK_UNLOCK=true on backend)
  const mockUnlock = async (plan) => {
    try {
      const response = await fetch(`${API_URL}/api/mock/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, plan }),
      });
      if (response.ok) {
        const data = await response.json();
        setUserStatus(data.userStatus);
        return data;
      }
    } catch (error) {
      console.error('Failed to mock unlock:', error);
    }
    return null;
  };

  // Reset user for testing
  const resetUser = async () => {
    try {
      const response = await fetch(`${API_URL}/api/mock/reset/${userId}`, {
        method: 'POST',
      });
      if (response.ok) {
        const data = await response.json();
        setUserStatus(data.userStatus);
        return data;
      }
    } catch (error) {
      console.error('Failed to reset user:', error);
    }
    return null;
  };

  const value = {
    userId,
    userStatus,
    loading,
    upgradeToBasic,
    upgradeToPro,
    recordViews,
    mockUnlock,
    resetUser,
    refreshStatus: fetchUserStatus,
    setUserStatus,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

export default UserContext;
